/**
 * Demo fixtures for the Profit sidebar tab (V5.2 mockup numbers — September).
 * Live Xero / profitability APIs are out of scope for this pass.
 */

export type ProfitDetailDim = 'vehicle' | 'job' | 'customer' | 'postcode' | 'run';

/** Preset chips, or a chosen calendar month (`month:YYYY-MM`, the Custom chip). */
export type ProfitReportPeriod =
  | 'yesterday'
  | 'last-week'
  | 'month-to-date'
  | 'last-month'
  | `month:${string}`;

export type ProfitBriefPeriod = 'yesterday' | 'this-week' | 'this-month';

export interface ProfitKpis {
  netProfitGbp: number;
  marginPct: number;
  retainedRevenueGbp: number;
  customerRevenueGbp: number;
  networkChargesGbp: number;
  ownCostsGbp: number;
  fuelGbp: number;
  driversGbp: number;
  standingGbp: number;
  /** Xero P&L "Hire of Motor Vehicle(s)" when books path. */
  hireOfMotorVehicleGbp?: number | null;
  /** Sum of Xero P&L Motor Expenses lines when books path. */
  motorExpensesGbp?: number | null;
  /** Xero P&L "Agency Staff Hire" when books path. */
  agencyStaffHireGbp?: number | null;
  /**
   * Ops path only: estimated network cost of outbound orders in view
   * (/merged-orders network summary). null = not configured, or no outbound
   * order could be costed yet (unknown, not £0).
   */
  networkEstimate?: {
    gbp: number;
    lowGbp: number;
    highGbp: number;
    costedOutboundOrders: number;
    /** Outbound orders with no rate for their month. */
    outboundWithoutRate: number;
    /** True when the cost is already inside net profit and margin (vehicle-day deduction). */
    deducted?: boolean;
  } | null;
  /** Revenue on vehicle-days without mileage data, left out of every other figure. */
  /**
   * Vehicle-days without mileage data, left out of the cards: their revenue,
   * and net profit over every day including them (their mileage-based costs
   * are missing, so it is higher than it should be).
   */
  revenueWithoutTelematics?: { gbp: number; vehicleDays: number; netProfitAllDaysGbp: number };
  workingDays: number;
  stampLabel: string;
}

export interface ProfitDailyPoint {
  day: number;
  /** Chart x-axis: weekday and DD/MM on two lines (`Mon\\n(07/09)`). */
  label: string;
  netProfitGbp: number;
  /** ISO date (YYYY-MM-DD) when the point maps to a calendar day. */
  date?: string;
  /** Slight emphasis (e.g. Yesterday chip → yesterday’s bar). */
  highlighted?: boolean;
  /** Every vehicle-day that day lacks mileage data: profit unknown, so no bar (not £0). */
  noMileageOnly?: true;
}

export interface ProfitDetailTable {
  heads: string[];
  rows: string[][];
  foot: string[];
  /** Column indexes treated as money / margin for colour. */
  moneyCols: number[];
  /** Column index that shows a MapPin affordance (vehicle reg / tracking id) — live only. */
  mapPinCol?: number;
  /**
   * Per-row keys aligned with `rows` (after the same sort as rows).
   * Used to open the vehicle/order map without parsing display cells.
   */
  rowMeta?: ProfitDetailRowMeta[];
  /** Column index for tonnage-class chip (e.g. 18t / Van) — V5 `.vt` style. */
  typeCol?: number;
  /** Column index for run name pill — V5 `.run-pill` style. */
  runPillCol?: number;
  /**
   * Span the foot[0] label across this many leading columns (e.g. Date+Vehicle).
   * Foot cells under the span should be empty strings.
   */
  footLabelColSpan?: number;
  /** Caveat shown under the table to every viewer (e.g. vehicle-days with no GPS). */
  note?: string;
  /** Pipeline caveat shown to admins only (e.g. jobs not yet in any customer row). */
  adminNote?: string;
}

/** Metadata for MapPin opens — parallel to ProfitDetailTable.rows. */
export type ProfitDetailRowMeta =
  | {
      kind: 'vehicle';
      vehicle: string;
      date: string;
      orderIds: string[];
      pickupOrderIds: string[];
      deliveryOrderIds: string[];
      /** Hover per column head: which figures the day's jobs used. */
      figureTitles?: Partial<Record<string, string>>;
    }
  | {
      kind: 'customer';
      /** Hover per column head: which figures the customer's jobs used. */
      figureTitles?: Partial<Record<string, string>>;
    }
  | {
      kind: 'job';
      orderId: string;
      vehicleObserved: string;
      vehiclePickup: string;
      date: string;
      /** Full truth order for map (coords, handoff, etc.). */
      order: import('../types/profitabilityTruth').TruthMergedOrderRow;
    };

export interface ProfitDataQualityNotice {
  title: string;
  body: string;
  actionLabel: string;
}

export interface ProfitBriefFinding {
  n: number;
  title: string;
  tier: 'confirmed' | 'estimated';
  body: string;
  impactLabel: string;
  impactNegative: boolean;
  caveat?: string;
  suggested: string;
  linkLabel: string;
}

export interface ProfitBriefBundle {
  coveringLabel: string;
  zoeSubtitle: string;
  generatedLabel: string;
  leadHtml: string;
  findings: ProfitBriefFinding[];
  askChips: string[];
}

/**
 * How the four KPI cards should be labelled.
 * - `demo_xero_style`: mock retained / network split (illustrative Xero-shaped copy)
 * - `ops_attributed`: live Phase 1 — TMS / vehicle-day attribution, never claim Xero
 * - `xero_books`: live Phase 2 — company KPIs from ingested Xero Accounting
 */
export type ProfitKpiMode = 'demo_xero_style' | 'ops_attributed' | 'xero_books';

/** Live Xero company-KPI strip notice (gaps / unavailable / error). */
export interface ProfitXeroKpiNotice {
  kind: 'from_xero' | 'unavailable' | 'error';
  reason?: string;
  /** Prefer P&L for company cards when present. */
  booksSource?: 'profit_and_loss' | 'invoice_rollup' | null;
  /** Exact P&L report window when booksSource is profit_and_loss. */
  pnlFrom?: string;
  pnlTo?: string;
  unmappedAccountCodes?: string[];
  unallocatedPurchaseCreditGbp?: number;
  partialRevenueMapping?: boolean;
}

/** Read-time Assumed £/job fill (declared rate — not live Xero ÷ local jobs). */
export interface ProfitRevenueImputationNotice {
  kind: 'assumed_avg_per_job';
  avgGbpPerJob: number;
  /** Months whose Xero figures by direction filled jobs (plan step 3); empty: the single average only. */
  monthsUsed?: Record<string, import('../services/profitabilityTruthClient').RevenueMonthUsed>;
  /** Jobs filled at the single average because no monthly figure applied. */
  filledAtSingleAverage?: number;
  referencePeriod: { start: string; end: string };
  provenance: {
    customer_revenue_gbp: number;
    job_count: number;
    job_count_source: string;
    computed_at: string;
  };
  ordersFilled: number;
  vehicleDaysFilled: number;
}

export interface ProfitPageBundle {
  sourcesLabel: string;
  /** Provenance mode for KPI strip copy / badges. Defaults to demo when omitted. */
  kpiMode?: ProfitKpiMode;
  /** Optional Xero KPI status when live (not demo). */
  xeroKpiNotice?: ProfitXeroKpiNotice | null;
  /** When Assumed £/job fill was applied to empty detail rows. */
  revenueImputation?: ProfitRevenueImputationNotice | null;
  /** One line per separate contract left out of these figures. */
  otherWork?: string[];
  kpis: ProfitKpis;
  dailySeries: ProfitDailyPoint[];
  chartTitle: string;
  dataQuality: ProfitDataQualityNotice | null;
  detail: Record<ProfitDetailDim, ProfitDetailTable>;
  notes: {
    freshness: string;
    dataQualityFollowUp: string;
    kpis: string;
    detail: string;
  };
}

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/**
 * Mon–Fri dates in a calendar month (local). Never crosses into the next month.
 * When `n` is set, returns at most the first `n` working days that fall in-month.
 */
export function workingDaysInMonth(year: number, monthIndex0: number, n?: number): Date[] {
  const out: Date[] = [];
  const d = new Date(year, monthIndex0, 1);
  while (d.getMonth() === monthIndex0) {
    const wd = d.getDay(); // 0 Sun … 6 Sat
    if (wd >= 1 && wd <= 5) {
      out.push(new Date(d));
      if (n !== undefined && out.length >= n) break;
    }
    d.setDate(d.getDate() + 1);
  }
  return out;
}

export function chartLabel(d: Date): string {
  // JS getDay: 0=Sun → map to Mon-first DOW index
  const monFirst = (d.getDay() + 6) % 7;
  const day = DOW[monFirst]!;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}\n(${dd}/${mm})`;
}

function shortDay(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mon = d.toLocaleString('en-GB', { month: 'short' });
  return `${dd} ${mon}`;
}

/** September 2026 MTD — 18 working days (through Thu 24 Sep). */
const SEP_2026_MTD_DAYS = workingDaysInMonth(2026, 8, 18);
const SEP_MTD_LAST = SEP_2026_MTD_DAYS[SEP_2026_MTD_DAYS.length - 1]!;

/** All Mon–Fri in August 2026 (21 days — month does not have 22). */
const AUG_2026_DAYS = workingDaysInMonth(2026, 7);

/** Months the demo fixtures cover — the Custom period list in demo mode. */
export const DEMO_PROFIT_MONTHS: Array<{ month: string; days: number }> = [
  { month: '2026-09', days: SEP_2026_MTD_DAYS.length },
  { month: '2026-08', days: AUG_2026_DAYS.length },
];

const DETAIL_VEHICLE: ProfitDetailTable = {
  heads: ['Date', 'Vehicle', 'Type', 'Miles', 'Pickup', 'Delivery', 'Jobs', 'Revenue', 'Fuel', 'Driver', 'Stand.', 'Profit', 'Margin'],
  rows: [
    [shortDay(SEP_MTD_LAST), 'DM21 AAA', '18t', '148', '8', '18', '44', '£742', '£142', '£310', '£140', '+£150', '20.2%'],
    [shortDay(SEP_MTD_LAST), 'DM22 BBB', '18t', '141', '7', '18', '40', '£675', '£136', '£310', '£140', '+£89', '13.2%'],
    [shortDay(SEP_MTD_LAST), 'DM71 CCC', '18t', '132', '6', '16', '38', '£641', '£128', '£310', '£140', '+£63', '9.8%'],
    [shortDay(SEP_MTD_LAST), 'DM21 DDD', '18t', '127', '5', '16', '36', '£607', '£122', '£310', '£140', '+£35', '5.8%'],
    [shortDay(SEP_MTD_LAST), 'DM19 EEE', '7.5t', '96', '5', '14', '24', '£405', '£74', '£286', '£96', '−£51', '−12.6%'],
    [shortDay(SEP_MTD_LAST), 'DM70 FFF', 'Van', '62', '4', '10', '14', '£236', '£31', '£198', '£58', '−£51', '−21.6%'],
  ],
  foot: ['6 of 8 vehicles active', '', '', '706', '35', '92', '196', '£3,306', '£633', '£1,724', '£714', '+£235', '7.1%'],
  moneyCols: [7, 8, 9, 10, 11, 12],
  typeCol: 2,
  footLabelColSpan: 2,
};

const DETAIL_JOB: ProfitDetailTable = {
  heads: ['Tracking ID', 'Customer', 'Vehicle', 'Lifts', 'Revenue', 'Own cost', 'Profit', 'Margin'],
  rows: [
    ['9900000412', 'Example Stone Co', 'DM21 AAA', '6', '£186', '£160', '+£26', '14.0%'],
    ['9900000398', 'Demo Timber Ltd', 'DM22 BBB', '4', '£142', '£127', '+£15', '10.6%'],
    ['9900000371', 'Sample Fixings', 'DM71 CCC', '3', '£98', '£92', '+£6', '6.1%'],
    ['9900000355', 'Mock Lighting Ltd', 'DM21 DDD', '5', '£121', '£123', '−£2', '−1.7%'],
    ['9900000288', 'Test Packaging Ltd', 'DM19 EEE', '2', '£54', '£69', '−£15', '−27.8%'],
    ['9900000197', 'example-gazebos', 'DM70 FFF', '1', '£38', '£51', '−£13', '−34.2%'],
  ],
  foot: ['6 jobs shown', '', '', '21', '£639', '£622', '+£17', '2.7%'],
  moneyCols: [4, 5, 6, 7],
};

const DETAIL_CUSTOMER: ProfitDetailTable = {
  heads: ['Customer', 'Jobs', 'Lifts', 'Revenue', 'Own cost', 'Profit', 'Margin'],
  rows: [
    ['Example Stone Co Ltd', '18', '62', '£4,820', '£3,910', '+£910', '18.9%'],
    ['Demo Timber Ltd', '14', '48', '£3,640', '£3,120', '+£520', '14.3%'],
    ['Sample Fixings Ltd', '11', '39', '£2,910', '£2,640', '+£270', '9.3%'],
    ['Mock Lighting Ltd', '9', '28', '£1,880', '£1,790', '+£90', '4.8%'],
    ['Test Packaging Ltd', '7', '21', '£1,240', '£1,380', '−£140', '−11.3%'],
    ['example-gazebos', '6', '14', '£860', '£1,020', '−£160', '−18.6%'],
  ],
  foot: ['6 of 42 customers', '65', '212', '£15,350', '£13,860', '+£1,490', '9.7%'],
  moneyCols: [3, 4, 5, 6],
};

const DETAIL_POSTCODE: ProfitDetailTable = {
  heads: ['Period', 'Postcode district', 'Pickups', 'Deliveries', 'Lifts', 'Miles', 'Revenue', 'Own cost', 'Profit', 'Margin'],
  rows: [
    ['2026-09', 'LS10 · Leeds', '10', '24', '58', '210', '£1,240', '£980', '+£260', '21.0%'],
    ['2026-09', 'M14 · Manchester', '8', '20', '46', '186', '£980', '£860', '+£120', '12.2%'],
    ['2026-09', 'HU3 · Hull', '6', '16', '38', '164', '£820', '£790', '+£30', '3.7%'],
    ['2026-09', 'S9 · Sheffield', '5', '14', '31', '148', '£710', '£740', '−£30', '−4.2%'],
    ['2026-09', 'YO10 · York', '16', '12', '24', '122', '£540', '£610', '−£70', '−13.0%'],
    ['2026-09', 'Unknown', '3', '9', '18', '96', '£390', '£470', '−£80', '−20.5%'],
  ],
  foot: ['6 districts', '', '36', '95', '215', '926', '£4,680', '£4,450', '+£230', '4.9%'],
  moneyCols: [6, 7, 8, 9],
  footLabelColSpan: 2,
};

const DETAIL_RUN: ProfitDetailTable = {
  heads: ['Period', 'Run', 'Vehicles', 'Pickups', 'Deliveries', 'Lifts', 'Miles', 'Revenue', 'Own cost', 'Profit', 'Margin'],
  rows: [
    ['2026-09', 'Run 1', '2', '14', '34', '72', '268', '£1,420', '£1,180', '+£240', '16.9%'],
    ['2026-09', 'Run 2', '2', '4', '8', '88', '410', '£1,860', '£1,720', '+£140', '7.5%'],
    ['2026-09', 'Run 3', '2', '12', '29', '54', '214', '£980', '£1,040', '−£60', '−6.1%'],
    ['2026-09', 'Unassigned', '2', '10', '22', '36', '158', '£656', '£677', '−£21', '−3.2%'],
  ],
  foot: ['4 runs', '', '8', '40', '93', '250', '1,050', '£4,916', '£4,617', '+£299', '6.1%'],
  moneyCols: [7, 8, 9, 10],
  runPillCol: 1,
};

const MTD_DAILY = [420, 510, 380, -140, 610, 470, 540, -90, 660, 580, 430, 700, 610, 520, -60, 590, 640, 490];

/** August demo series — length matches in-month working days (21). */
const AUG_DAILY = [...MTD_DAILY, 510, 440, 390].slice(0, AUG_2026_DAYS.length);

const NOTES = {
  freshness:
    'How fresh the cost side can be depends on finance entry, not on us. Palletways data is live. Xero is only as current as the day bills are entered. On sample ACCPAY bills, invoice date → last update is typically 0–2 days — closer to “a few days behind” than “month end only”.',
  dataQualityFollowUp:
    'The same notice will carry Xero gaps once connected — an unmapped account code, a bill not yet entered, a vehicle with no depreciation set up. It always states which way the number is wrong, not just that something is missing.',
  kpis:
    'Net profit is revenue less network charges and own costs. Margin is profit ÷ (revenue − network). Own costs split fuel, drivers and standing from the demo cost model until live Xero feeds are wired.',
  detail:
    'Miles, pickups, deliveries and lifts from Palletways and Samsara. Revenue from sales invoices. Fuel and standing from purchase / asset costs in the demo set. Vehicle and depot are not Xero tracking options — allocation still needs operational data.',
};

const BRIEF_FINDINGS_WEEK: ProfitBriefFinding[] = [
  {
    n: 1,
    title: 'DM70 FFF is not covering its standing cost',
    tier: 'confirmed',
    body: 'The van has averaged 14 lifts a day against a fleet average of 33, on 62 miles. It has been negative on 14 of 18 days.',
    impactLabel: '−£890 month to date',
    impactNegative: true,
    caveat:
      'Before acting: £58 a day of this is standing cost, which does not go away if the van is parked. Taking it off the roster saves the fuel and driver, not the standing. The real question is whether the 14 lifts it carries could ride on the 18t runs instead.',
    suggested: "Test whether DM70 FFF's drops fit inside the existing DM21 AAA and DM22 BBB rounds.",
    linkLabel: 'Open in Profit →',
  },
  {
    n: 2,
    title: 'Fuel is up £1,240 on flat volume',
    tier: 'confirmed',
    body: 'Lifts are within 2% of last month but fuel spend is 9% higher. The five most expensive vehicle-days are all hub trunk runs to Lichfield, and three of the five returned empty.',
    impactLabel: '−£1,240 vs August',
    impactNegative: true,
    suggested: 'Review the three empty return legs against collections that were booked the same afternoon.',
    linkLabel: 'Open in Telematics →',
  },
  {
    n: 3,
    title: 'Missed AM promises cost £312 this month',
    tier: 'estimated',
    body: '41 consignments on next-day-by-1200 were delivered after the cut-off. Two thirds were on the south round, which has left the depot after 09:15 on 11 days this month.',
    impactLabel: '−£312 credited back',
    impactNegative: true,
    caveat:
      'Estimated, not confirmed: this uses the £7.50 AM fee from the rules sheet applied to delivery times from Palletways. Confirmed once the fee schedule is matched to credit descriptions.',
    suggested: 'Bring the south round dispatch forward to 08:15 and re-check next week.',
    linkLabel: 'Open in Orders →',
  },
];

const BRIEF_ASK_CHIPS = [
  'Why is DM19 EEE losing money?',
  'Which customers are below 10% margin?',
  'Did yesterday cover its costs?',
  "What would DM70 FFF's drops cost on the 18t?",
];

function scaleKpis(base: ProfitKpis, factor: number, stamp: string, workingDays: number): ProfitKpis {
  const round = (n: number) => Math.round(n * factor);
  const retained = round(base.retainedRevenueGbp);
  const net = round(base.netProfitGbp);
  return {
    ...base,
    netProfitGbp: net,
    retainedRevenueGbp: retained,
    customerRevenueGbp: round(base.customerRevenueGbp),
    networkChargesGbp: round(base.networkChargesGbp),
    ownCostsGbp: round(base.ownCostsGbp),
    fuelGbp: round(base.fuelGbp),
    driversGbp: round(base.driversGbp),
    standingGbp: round(base.standingGbp),
    marginPct: retained > 0 ? Math.round((net / retained) * 1000) / 10 : 0,
    workingDays,
    stampLabel: stamp,
  };
}

const MTD_KPIS: ProfitKpis = {
  netProfitGbp: 8820,
  marginPct: 11.1,
  retainedRevenueGbp: 79380,
  customerRevenueGbp: 205740,
  networkChargesGbp: 126360,
  ownCostsGbp: 70560,
  fuelGbp: 14760,
  driversGbp: 38160,
  standingGbp: 17640,
  workingDays: 18,
  stampLabel: `${SEP_MTD_LAST.getDate()} September 2026 · 18 working days · synced 06:15`,
};

function calendarDaysEnding(end: Date, n: number): Date[] {
  const out: Date[] = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(end);
    d.setDate(end.getDate() - i);
    out.push(d);
  }
  return out;
}

function dailyFromValues(
  values: number[],
  titlePrefix: string,
  dates: Date[],
  opts?: { highlightLast?: boolean },
): { series: ProfitDailyPoint[]; chartTitle: string } {
  const len = Math.min(values.length, dates.length);
  return {
    chartTitle: titlePrefix,
    series: values.slice(0, len).map((netProfitGbp, i) => {
      const d = dates[i]!;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return {
        day: i + 1,
        label: chartLabel(d),
        netProfitGbp,
        date: `${y}-${m}-${day}`,
        ...(opts?.highlightLast && i === len - 1 ? { highlighted: true } : {}),
      };
    }),
  };
}

/** Report fixtures keyed by period (Today is not offered). */
export function getMockProfitReport(period: ProfitReportPeriod): ProfitPageBundle {
  const baseDaily = dailyFromValues(
    MTD_DAILY,
    'Daily net profit: September to date',
    SEP_2026_MTD_DAYS,
  );
  let kpis = MTD_KPIS;
  let daily = baseDaily;

  if (period === 'yesterday') {
    const y = SEP_MTD_LAST;
    kpis = scaleKpis(
      MTD_KPIS,
      1 / 18,
      `${y.getDate()} September 2026 · 1 working day · synced 06:15`,
      1,
    );
    const week = calendarDaysEnding(y, 7);
    // Demo: reuse recent working-day profits on weekdays; weekends stay £0.
    let wi = 0;
    const weekVals = week.map((d) => {
      const wd = d.getDay();
      if (wd === 0 || wd === 6) return 0;
      const v = MTD_DAILY[MTD_DAILY.length - 5 + wi] ?? 400;
      wi += 1;
      return v;
    });
    daily = dailyFromValues(weekVals, 'Daily net profit: last 7 days', week, { highlightLast: true });
  } else if (period === 'last-week') {
    const y = SEP_MTD_LAST;
    const week = calendarDaysEnding(y, 7);
    kpis = scaleKpis(
      MTD_KPIS,
      5 / 18,
      '21–25 September 2026 · 5 working days · synced 06:15',
      5,
    );
    let wi = 0;
    const weekVals = week.map((d) => {
      const wd = d.getDay();
      if (wd === 0 || wd === 6) return 0;
      const v = MTD_DAILY[MTD_DAILY.length - 5 + wi] ?? 400;
      wi += 1;
      return v;
    });
    daily = dailyFromValues(weekVals, 'Daily net profit: last 7 days', week);
  } else if (
    period === 'last-month'
    // Custom month: September is the month-to-date fixture (default above); any
    // other month falls back to the August fixture.
    || (period.startsWith('month:') && period !== 'month:2026-09')
  ) {
    const days = AUG_2026_DAYS.length;
    kpis = scaleKpis(
      MTD_KPIS,
      days / 18,
      `August 2026 · ${days} working days · synced 06:15`,
      days,
    );
    daily = dailyFromValues(AUG_DAILY, 'Daily net profit: August', AUG_2026_DAYS);
  }

  return {
    sourcesLabel: 'Xero · Palletways · Samsara',
    kpiMode: 'demo_xero_style',
    kpis,
    dailySeries: daily.series,
    chartTitle: daily.chartTitle,
    dataQuality: {
      title: '2 vehicle-days missing telematics mileage',
      body: 'DM19 EEE on 22 and 23 September. Fuel shows as zero for those days; standing cost is still applied. Included in the totals — margin for those two days reads higher than it was.',
      actionLabel: 'Show the 2 days →',
    },
    detail: {
      vehicle: DETAIL_VEHICLE,
      job: DETAIL_JOB,
      customer: DETAIL_CUSTOMER,
      postcode: DETAIL_POSTCODE,
      run: DETAIL_RUN,
    },
    notes: NOTES,
  };
}

/** ZOE brief fixtures — content follows the Covering period chips. */
export function getMockProfitBrief(period: ProfitBriefPeriod): ProfitBriefBundle {
  if (period === 'yesterday') {
    const y = SEP_MTD_LAST;
    const label = `Daily brief · ${shortDay(y)}`;
    return {
      coveringLabel: label,
      zoeSubtitle: label,
      generatedLabel: 'Generated 06:15 · yesterday',
      leadHtml:
        'Yesterday you kept <b>£4,410</b> after the network was paid and <b>£490</b> reached the bottom line — an <b>11.1% margin</b>. DM70 FFF and DM19 EEE were both negative; the 18t fleet covered the day. <b>Two things worth a look this morning.</b>',
      findings: [
        {
          ...BRIEF_FINDINGS_WEEK[0]!,
          body: 'Yesterday the van ran 62 miles for 14 lifts and finished −£51 after standing. Same pattern as the month-to-date story.',
          impactLabel: '−£51 yesterday',
        },
        {
          ...BRIEF_FINDINGS_WEEK[1]!,
          n: 2,
          title: 'Empty return on the Lichfield trunk',
          body: 'DM21 AAA returned from the hub with no collection booked for the afternoon. Fuel for that leg sits in yesterday’s own costs.',
          impactLabel: '−£38 fuel on empty return',
        },
      ],
      askChips: BRIEF_ASK_CHIPS,
    };
  }

  if (period === 'this-month') {
    const label = 'Monthly brief · September to date';
    return {
      coveringLabel: label,
      zoeSubtitle: label,
      generatedLabel: 'Generated 06:15 · this month',
      leadHtml:
        'Month to date you kept <b>£79,380</b> of £205,740 in customer revenue after the network was paid, and <b>£8,820</b> of that reached the bottom line — an <b>11.1% margin</b>. Two vehicles account for most of the drag, and fuel is up on flat volume. <b>Three things worth your time this month.</b>',
      findings: BRIEF_FINDINGS_WEEK,
      askChips: BRIEF_ASK_CHIPS,
    };
  }

  // this-week
  const label = 'Weekly brief · 21–27 September';
  return {
    coveringLabel: label,
    zoeSubtitle: label,
    generatedLabel: 'Generated 06:15, Monday 28 September',
    leadHtml:
      'This week you kept pace with the month-to-date margin (<b>11.1%</b>). DM70 FFF stayed negative, fuel on hub trunks is still elevated, and AM promise credits are accumulating on the south round. <b>Three things worth your time this week.</b>',
    findings: BRIEF_FINDINGS_WEEK,
    askChips: BRIEF_ASK_CHIPS,
  };
}

export function formatProfitGbp(n: number): string {
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString('en-GB', { maximumFractionDigits: 0 });
  if (n < 0) return `−£${formatted}`;
  return `£${formatted}`;
}

export function isNegativeMoneyCell(text: string): boolean {
  const t = text.trim();
  return t.startsWith('−') || t.startsWith('-') || /−\d/.test(t);
}

export function isPositiveMoneyCell(text: string): boolean {
  const t = text.trim();
  if (isNegativeMoneyCell(t)) return false;
  return t.startsWith('+') || (t.includes('%') && !isNegativeMoneyCell(t));
}
